import * as React from 'react'

import { clsx } from 'clsx'
import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import {
  IconButton,
  PanelLeftOpenIcon,
  Popover,
  Text,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { useDelayedHover } from '../../hooks/ui/useDelayedHover'
import { EASE_IN_OUT } from '../../util/easingRules'

import styles from './Sidepanel.module.css'

import type { JSX } from 'react'

type SidepanelCollapsibleContextValue = {
  /** Whether the content is currently rendered inside the hover preview popover */
  isPreview: boolean
  /** Close the preview popover (noop when not in preview) */
  closePreview: () => void
}

const SidepanelCollapsibleContext =
  React.createContext<SidepanelCollapsibleContextValue>({
    isPreview: false,
    closePreview: () => {},
  })

/**
 * Hook to read the collapsible context from inside Sidepanel children.
 * Returns `{ isPreview, closePreview }`.
 * Safe to call in non-collapsible panels — defaults to `{ isPreview: false, closePreview: noop }`.
 */
export function useSidepanelCollapsible(): SidepanelCollapsibleContextValue {
  return React.useContext(SidepanelCollapsibleContext)
}

const PANEL_WIDTHS = { small: 250, medium: 268, large: 268 } as const
const COLLAPSED_WIDTH = 40
const COLLAPSE_COOLDOWN_MS = 600

type SidepanelProps = {
  /**
   * The content of the panel
   */
  children: React.ReactNode
  /**
   * The class name of the panel
   */
  className?: string
  /**
   * Accessible label for the panel
   */
  label: string
  /**
   * The size/width of the panel
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Enable collapsible mode with animated expand/collapse
   */
  collapsible?: boolean
  /**
   * Whether the panel is expanded (only used when `collapsible` is true)
   */
  open?: boolean
  /**
   * Callback when the panel's expanded state changes
   */
  onOpenChange?: (open: boolean) => void
} & Omit<React.ComponentProps<'aside'>, 'children'>

/**
 * The main Sidepanel component.
 * When `collapsible` is true, renders an animated wrapper that toggles between
 * a collapsed icon strip (with hover preview) and the full expanded panel.
 */
const Sidepanel = React.forwardRef<React.ElementRef<'aside'>, SidepanelProps>(
  (
    {
      children,
      className,
      label,
      size = 'medium',
      collapsible = false,
      open = true,
      onOpenChange,
      ...rest
    }: SidepanelProps,
    ref,
  ) => {
    // On mobile, a collapsible panel renders the plain stacked sidepanel while
    // open and nothing while collapsed; its consumer owns the open control.
    const isMobile = useIsMobileScreenSize()

    const aside = (
      <aside
        ref={ref}
        aria-label={label}
        className={clsx(
          styles.sidepanel,
          size === 'small' && styles.small,
          size === 'large' && styles.large,
          className,
        )}
        data-size={size}
        {...rest}
      >
        {children}
      </aside>
    )

    if (!collapsible || (isMobile && open)) {
      return aside
    }

    if (isMobile) {
      return null
    }

    return (
      <CollapsibleWrapper
        open={open}
        onOpenChange={onOpenChange}
        size={size}
        aside={aside}
      >
        {children}
      </CollapsibleWrapper>
    )
  },
)

Sidepanel.displayName = 'Sidepanel'

type CollapsibleWrapperProps = {
  open: boolean
  onOpenChange?: (open: boolean) => void
  size: 'small' | 'medium' | 'large'
  aside: React.ReactNode
  children: React.ReactNode
}

function CollapsibleWrapper({
  open,
  onOpenChange,
  size,
  aside,
  children,
}: CollapsibleWrapperProps): JSX.Element {
  const panelWidth = PANEL_WIDTHS[size]

  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false)

  // Cooldown prevents the preview from opening immediately after collapsing,
  // since the collapsed icon may appear under the cursor and trigger mouseenter.
  const collapseCooldownRef = React.useRef(false)

  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => {
      if (!collapseCooldownRef.current) {
        setIsPreviewOpen(true)
      }
    },
    onClose: () => setIsPreviewOpen(false),
    openDelayDuration: 500,
    closeDelayDuration: 400,
    isOpen: isPreviewOpen,
  })

  const handleExpand = React.useCallback(() => {
    setIsPreviewOpen(false)
    onOpenChange?.(true)
  }, [onOpenChange])

  // Apply cooldown when `open` transitions to false (panel collapsed)
  const prevOpenRef = React.useRef(open)
  React.useEffect(() => {
    if (prevOpenRef.current && !open) {
      collapseCooldownRef.current = true
      const timer = setTimeout(() => {
        collapseCooldownRef.current = false
      }, COLLAPSE_COOLDOWN_MS)
      return (): void => clearTimeout(timer)
    }
    prevOpenRef.current = open
    return undefined
  }, [open])

  // Context values for expanded vs preview rendering
  const expandedContext = React.useMemo<SidepanelCollapsibleContextValue>(
    () => ({
      isPreview: false,
      closePreview: (): void => {},
    }),
    [],
  )

  const previewContext = React.useMemo<SidepanelCollapsibleContextValue>(
    () => ({
      isPreview: true,
      closePreview: (): void => setIsPreviewOpen(false),
    }),
    [],
  )

  return (
    <m.div
      className={styles.collapsibleWrapper}
      animate={{ width: open ? panelWidth : COLLAPSED_WIDTH }}
      transition={{ duration: 0.2, ease: EASE_IN_OUT.cubic }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!open ? (
          <m.div
            key="collapsed"
            className={styles.collapsedStrip}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Popover
              open={isPreviewOpen}
              onOpenChange={setIsPreviewOpen}
              modal={false}
            >
              <Popover.Anchor>
                <IconButton
                  label="Open panel"
                  variant="secondary"
                  size="medium"
                  className={styles.collapsedIcon}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleExpand}
                >
                  <PanelLeftOpenIcon />
                </IconButton>
              </Popover.Anchor>
              <Popover.Content
                side="right"
                align="start"
                className={styles.previewPopover}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onPointerDownOutside={() => setIsPreviewOpen(false)}
              >
                <SidepanelCollapsibleContext.Provider value={previewContext}>
                  {children}
                </SidepanelCollapsibleContext.Provider>
              </Popover.Content>
            </Popover>
          </m.div>
        ) : (
          <m.div
            key="expanded"
            className={styles.expandedPanel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <SidepanelCollapsibleContext.Provider value={expandedContext}>
              {aside}
            </SidepanelCollapsibleContext.Provider>
          </m.div>
        )}
      </AnimatePresence>
    </m.div>
  )
}

/**
 * The header component for the sidepanel
 */
const SidepanelHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'header'>
>(({ className, ...props }, ref) => {
  return (
    <header ref={ref} className={clsx(styles.header, className)} {...props} />
  )
})
SidepanelHeader.displayName = 'Sidepanel.Header'

/**
 * The content component for scrollable content in the sidepanel
 */
const SidepanelContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx(styles.content, className)} {...props} />
  )
})
SidepanelContent.displayName = 'Sidepanel.Content'

/**
 * The footer component for the sidepanel
 */
const SidepanelFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'footer'>
>(({ className, ...props }, ref) => {
  return (
    <footer ref={ref} className={clsx(styles.footer, className)} {...props} />
  )
})
SidepanelFooter.displayName = 'Sidepanel.Footer'

/**
 * List component for items in the sidepanel
 */
type SidepanelListProps = {
  children: React.ReactNode
  className?: string
  /**
   * The role for the list (e.g., 'listbox' for selectable items)
   */
  role?: string
  /**
   * Accessible label for the list
   */
  'aria-label'?: string
} & React.ComponentProps<'ul'>

const SidepanelList = React.forwardRef<
  React.ElementRef<'ul'>,
  SidepanelListProps
>(({ children, className, ...rest }, ref) => {
  return (
    <ul ref={ref} className={clsx(styles.list, className)} {...rest}>
      {children}
    </ul>
  )
})

SidepanelList.displayName = 'Sidepanel.List'

/**
 * List item component for the sidepanel
 */
type SidepanelListItemProps = {
  /**
   * Whether this item is selected
   */
  selected?: boolean
  /**
   * The role for the list item (e.g., 'option' for selectable items)
   */
  role?: string
  /**
   * Whether this item is selected (for accessibility)
   */
  'aria-selected'?: boolean
} & React.ComponentProps<'li'>

const SidepanelListItem = React.forwardRef<
  React.ElementRef<'li'>,
  SidepanelListItemProps
>(({ children, className, selected, ...props }, ref) => {
  return (
    <li
      ref={ref}
      className={clsx(
        styles.listItem,
        selected && styles.listItemSelected,
        className,
      )}
      {...props}
    >
      {children}
    </li>
  )
})

SidepanelListItem.displayName = 'Sidepanel.ListItem'

/**
 * Title component for the sidepanel
 */
const SidepanelTitle = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentProps<'div'>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx(styles.title, className)} {...props}>
      <Text size="lg" weight="medium">
        {children}
      </Text>
    </div>
  )
})
SidepanelTitle.displayName = 'Sidepanel.Title'

const SidepanelObject = Object.assign(Sidepanel, {
  Header: SidepanelHeader,
  Content: SidepanelContent,
  Footer: SidepanelFooter,
  List: SidepanelList,
  ListItem: SidepanelListItem,
  Title: SidepanelTitle,
})

export { SidepanelObject as Sidepanel }
export type { SidepanelProps, SidepanelListProps, SidepanelListItemProps }
